import { useEffect, useState } from 'react';
import { getNotificationPrefs, updateNotificationPrefs, type NotificationPrefs } from '@lib/api/portal';

const Toggle = ({ on, onToggle }: { on: boolean; onToggle: () => void }) => (
  <button
    onClick={onToggle}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none ${on ? 'bg-[#C77D3F]' : 'bg-gray-200'}`}
    role="switch"
    aria-checked={on}
  >
    <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
);

interface PrefRow {
  label: string;
  desc: string;
  emailKey: keyof NotificationPrefs;
}

const PREF_ROWS: PrefRow[] = [
  { label: 'New Invoices', desc: 'When a new invoice is issued to your account', emailKey: 'invoice_notifications' },
  { label: 'Schedule Updates', desc: 'When crew visits are added, changed, or cancelled', emailKey: 'schedule_notifications' },
  { label: 'New Documents', desc: 'Agreements, contracts, and project documents sent for your review', emailKey: 'document_notifications' },
];

const defaultPrefs = (): NotificationPrefs => ({
  email_enabled: true,
  sms_enabled: false,
  rcs_enabled: false,
  hours_reminder_enabled: true,
  invoice_notifications: true,
  schedule_notifications: true,
  document_notifications: true,
  payment_reminders: true,
});

export const ClientNotificationSettingsPage = () => {
  const [prefs, setPrefs] = useState<NotificationPrefs>(defaultPrefs());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getNotificationPrefs()
      .then(p => setPrefs({ ...defaultPrefs(), ...p }))
      .catch(() => setError("Failed to load notification settings."))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (key: keyof NotificationPrefs) => {
    setPrefs(p => ({ ...p, [key]: !p[key] }));
    setSaved(false);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateNotificationPrefs(prefs);
      setSaved(true);
    } catch {
      setError('Could not save — check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100" />)}</div>;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-[#2C1810]">Notifications</h1>
        <p className="text-sm text-gray-500 mt-0.5">Choose how OchrePoint keeps you informed.</p>
      </div>

      {error && (
        <div className="px-4 py-3 text-sm text-red-700 flex items-center gap-2 bg-red-50 rounded-xl border border-red-200">
          <span className="material-symbols-outlined text-base">error</span>{error}
        </div>
      )}

      {/* Channel toggles */}
      <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        <div className="p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-blue-600 text-xl">email</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Email Notifications</p>
              <p className="text-xs text-gray-500">Updates sent to your email address</p>
            </div>
          </div>
          <Toggle on={prefs.email_enabled} onToggle={() => toggle('email_enabled')} />
        </div>
      </div>

      {/* Per-type toggles */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Notification Types</p>
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {PREF_ROWS.map(row => (
            <div key={row.emailKey} className="p-4 flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{row.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{row.desc}</p>
              </div>
              <Toggle
                on={prefs.email_enabled && !!prefs[row.emailKey]}
                onToggle={() => toggle(row.emailKey)}
              />
            </div>
          ))}
        </div>
      </div>

      {saved && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          <span className="material-symbols-outlined text-base">check_circle</span>
          Notification preferences saved.
        </div>
      )}

      <button
        onClick={save}
        disabled={busy}
        className="w-full bg-[#C77D3F] text-white rounded-xl py-3 text-sm font-semibold hover:bg-[#b36e35] disabled:opacity-50 transition-colors"
      >
        {busy ? 'Saving…' : 'Save Preferences'}
      </button>
    </div>
  );
};
